
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Loader2 } from 'lucide-react';
import { useKeycloakContext } from '@/contexts/KeycloakContext';
import { CHAT_OIDC_LOGIN_URL } from '@/services/chatApi';
import { useToast } from '@/hooks/use-toast';

const FreeAI = () => {
  const { isLoading: authLoading, isAuthenticated, user } = useKeycloakContext();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showIframe, setShowIframe] = useState(false);
  const [iframeKey, setIframeKey] = useState(Date.now());

  // Ensure we have fresh authentication state
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      console.log('User not authenticated, redirecting to login');
      toast({
        title: "请先登录",
        description: "访问Free-AI功能需要登录账户"
      });
      navigate('/auth/login');
      return;
    }

    // Force iframe refresh when authentication state changes
    if (isAuthenticated && user) {
      console.log('User authenticated, loading chat.nisoo.com in iframe');
      setIframeKey(Date.now()); // Generate new key to force iframe reload
      setShowIframe(true);
    }
  }, [authLoading, isAuthenticated, navigate, user]);

  // Add message listener for iframe communication
  useEffect(() => {
    const handleMessage = (event) => {
      // Only process messages from chat.nisoo.com
      if (event.origin !== new URL(CHAT_OIDC_LOGIN_URL).origin) {
        return;
      }

      // Handle specific messages from the iframe
      if (event.data?.type === 'AUTH_ERROR') {
        console.error('Auth error from chat iframe:', event.data);
        toast({
          title: "认证错误",
          description: "聊天服务认证失败，请重新登录",
          variant: "destructive"
        });
        // Reload iframe to try again
        setIframeKey(Date.now());
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [toast]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">验证登录状态...</span>
          </div>
        </div>
        {!showIframe && <Footer />}
      </div>
    );
  }

  if (showIframe) {
    // Add Keycloak token as query parameter to help with authentication
    const keycloak = (window as any).Keycloak;
    const token = keycloak?.token;
    const chatUrl = token 
      ? `${CHAT_OIDC_LOGIN_URL}?token=${encodeURIComponent(token)}`
      : CHAT_OIDC_LOGIN_URL;
      
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <iframe
          key={iframeKey}
          src={chatUrl}
          className="flex-grow w-full h-[calc(100vh-4rem)]"
          title="Nisoo Chat"
          frameBorder="0"
          onLoad={() => console.log('Chat iframe loaded')}
          onError={(e) => console.error('Chat iframe error:', e)}
        />
      </div>
    );
  }

  return null;
};

export default FreeAI;
